/**
 * ProjectModal.jsx
 *
 * This file contains the ProjectModal React component which renders a modal dialog
 * for creating and editing project details, including project fields, views,
 * linked folders, and other metadata.
 */

import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react"
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation } from "@apollo/client";
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton } from "@mui/material"
import { CREATE_PROJECT, UPDATE_PROJECT } from "../../../graphql/mutations/ProjectEditContext";
import { useSnackbar } from "../../../hooks";
import { FolderSelector, GrayButton, PrimaryButton, TextAreaFull, TextField } from "../../common";
import FramedBox from "../common/FramedBox";
import ProjectViewSelector from "../ProjectViews/ProjectViewSelector";
import { default as ProjectViewUtils } from "../ProjectViews/projectViewUtils";
import ProjectViewsModal from "../ProjectViewsEditor/ProjectViewsModal";

// Fallback object for a new project. Used when no existing project is passed in.
// 'linkedFolderName' is used only for display; not part of the saved payload.
const initialProject = (siteId) => ({
  description: "",
  linkedFolderId: null,
  linkedFolderName: "",
  name: "",
  projectFields: [],
  projectViews: [],
  siteId,
});

/**
 * ProjectModal component.
 *
 * Renders a modal dialog that allows users to create or edit a project.
 * The modal supports editing project name, description, linked folder,
 * and managing project views.
 *
 * @param {object} props - Component props.
 * @param {boolean} props.open - Controls whether the modal is open.
 * @param {function} [props.onClose] - Callback fired when the modal should be closed.
 * @param {function} [props.onSaved] - Callback fired after a successful save with the updated project.
 * @param {object} [props.project] - Project data to edit. If omitted, creates a new project.
 * @param {number} props.siteId - The site ID associated with the project.
 *
 * @returns {JSX.Element|null} The rendered modal component or null if not initialized.
 */
const ProjectModal = ({
  onClose,
  onSaved,
  open,
  project,
  siteId,
}) => {
  const intl = useIntl();
  const snackbar = useSnackbar();

  const selectedViewIdRef = useRef(null);

  const [formErrors, setFormErrors] = useState({});
  const [internalProject, setInternalProject] = useState();
  const [showProjectViewsModal, setShowProjectViewsModal] = useState(false);

  // Memoize project tab data to avoid unnecessary recalculations.
  // Tabs depend on the type-specific structure of project fields and views.
  const projectTabs = useMemo(() => (
    ProjectViewUtils.getProjectTabsData(internalProject)
  ), [internalProject?.projectFields, internalProject?.projectViews]);

  const [createProject, { data: createProjectData, loading: createProjectLoading, error: createProjectError }] = useMutation(CREATE_PROJECT);


  //const [updateProject, { data: updateData, loading: loadingData, error: updateError }] = useMutation(UPDATE_PROJECT);


  const [updateProject, { loading: loadingData, error: updateError }] =
    useMutation(UPDATE_PROJECT, {
      onCompleted: (data) => {

        const updateResult = data?.projects?.updateProject;

        if (updateResult?.success) {
          snackbar.success(<FormattedMessage id="project.updated" />);
          onSaved?.(updateResult.project);
        } else {
          snackbar.error(<FormattedMessage id="project.errorSave" />);
        }
      },
      onError: (err) => {
        snackbar.error(<FormattedMessage id="project.errorSave" />);
      }
    });


  const isSubmitting = createProjectLoading || loadingData;

  const clamp = (number, min, max) => Math.max(min, Math.min(number, max));

  const buildProjectPayload = (project) => {
    const { projectId, siteId, name, description, linkedFolderId, projectFields, projectViews } = project;

    const payload = {
      // 'projectId' is included only for update operations.
      projectId,

      // 'siteId' is required when creating a new project.
      siteId,

      name,
      description,
      linkedFolderId,

      projectFields: projectFields.map(({ fieldId, name, type, jsonFieldSettings }) => ({
        id: fieldId,
        name,
        type,
        settings: jsonFieldSettings, // Field structure is type specific
      })),

      projectViews: projectViews.map(({ viewId, name, type, jsonViewSettings }) => ({
        id: clamp(viewId, 0, Number.MAX_SAFE_INTEGER),
        name,
        type,
        settings: jsonViewSettings, // View structure is type specific
      })),
    };

    return payload;
  };

  const getFolderName = folder => folder ? (folder.name || folder.folderName) : "";

  const handleChangeDescription = (e) => {
    setInternalProject((prev) => ({ ...prev, description: e.target.value }));
  };

  const handleChangeTitle = (e) => {
    setInternalProject((prev) => ({ ...prev, name: e.target.value }));
    clearFormError("name");
  };

  const handleClose = () => onClose?.();

  const handleCreate = () => handleSubmit(createProject);

  const handleLinkedFolderSelected = (folder) => {
    const { folderId: linkedFolderId } = folder;
    const linkedFolderName = getFolderName(folder);

    // Store the folder ID (for saving) and the name (for display only).
    setInternalProject((prev) => ({ ...prev, linkedFolderId, linkedFolderName }));
  };

  const handleSubmit = (mutationFn) => {
    const validation = validateProject(internalProject);
    if (validation.hasErrors) {
      showValidationErrors(validation.errors);
      return;
    }

    clearFormErrors();
    const payload = buildProjectPayload(internalProject);
    mutationFn({ variables: { project: payload } });
  };

  const handleViewsUpdate = (updatedViews) => {
    setInternalProject((prev) => ({ ...prev, projectViews: [...updatedViews] }));
    hideViewsModal();
  };

  // Workaround: Prevent excessive re-renders in @table-library/react-table 
  // when Shift is pressed during input operations.
  const handleShiftKeyDown = (e) => e.shiftKey && e.stopPropagation();

  // Workaround: Prevent excessive re-renders in @table-library/react-table 
  // when Shift is pressed during input operations.
  const handleShiftKeyUp = (e) => (e.key === "Shift") && e.stopPropagation();

  const handleUpdate = () => handleSubmit(updateProject);

  const hideViewsModal = () => {
    selectedViewIdRef.current = null;
    setShowProjectViewsModal(false);
  }

  const showViewsModal = (viewId = null) => {
    selectedViewIdRef.current = viewId;
    setShowProjectViewsModal(true);
  }

  const clearFormError = (errorKey) => {
    setFormErrors(prev => {
      if (!prev[errorKey]) return prev;
      const { [errorKey]: _, ...rest } = prev;
      return rest;
    });
  };

  const clearFormErrors = () => setFormErrors({});

  // Shows validation errors in a snacbar.
  const showValidationErrors = (validationErrors) => {
    setFormErrors(validationErrors);

    snackbar.error(
      <div>
        {Object.values(validationErrors).map((error, index) => (
          <div key={index}>{error}</div>
        ))}
      </div>
    );
  };

  // Validates project data.
  const validateProject = (project) => {
    const errors = {};

    if (!project.name?.trim()) {
      errors.name = <FormattedMessage id="project.validation.nameRequired" />;
    }

    return {
      hasErrors: Object.keys(errors).length > 0,
      errors,
    };
  };

  useEffect(() => {
    const createResult = createProjectData?.projects?.createProject;

    if (createResult?.success) {
      snackbar.success(<FormattedMessage id="project.created" />);
      onSaved?.(createResult.project);
    } else if (createProjectError || (createProjectData && !createResult?.success)) {
      snackbar.error(<FormattedMessage id="project.errorSave" />);
    }
  }, [createProjectData, createProjectError, onSaved, snackbar]);

  useEffect(() => {
    // Initialize internal state when project changes.
    if (project) {
      setInternalProject(project);
    } else {
      setInternalProject(initialProject(siteId))
    }
  }, [project, siteId]);

  if (!internalProject) {
    return null;
  }

  // We consider it edit mode if a projectId is present.
  const isEditMode = Boolean(project?.projectId);


  return (
    <>
      <Dialog
        fullWidth={true}
        maxWidth={"sm"}
        onClose={handleClose}
        onKeyDown={handleShiftKeyDown}
        onKeyUp={handleShiftKeyUp}
        open={open}
      >

        <DialogTitle>
          <FormattedMessage id={isEditMode ? "project.editproject" : "project.newproject"} />
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <TextField
                error={!!formErrors.name}
                // Validation errors are shown in a snackbar, not using helperText.
                // helperText={formErrors.name}
                label={<FormattedMessage id="project.project" />}
                onChange={handleChangeTitle}
                value={internalProject.name}
              />
            </Grid>
            <Grid item xs={12}>
              <TextAreaFull
                label={<FormattedMessage id="project.description" />}
                maxRows="6"
                minRows="6"
                name="description"
                onChange={handleChangeDescription}
                placeholder={intl.formatMessage({ id: "project.description" })}
                shrink
                InputLabelProps={{
                  sx: { "&.MuiFormLabel-root": { fontSize: "12px", } }
                }}
                value={internalProject.description}
              />
            </Grid>
            <Grid item xs={12}>
              <FolderSelector
                allowClear
                initialFolderId={internalProject.linkedFolderId}
                label={<FormattedMessage id="project.linkedFolderSelector.label" />}
                onChange={handleLinkedFolderSelected}
                value={{ name: internalProject.linkedFolderName }}
              />
            </Grid>
            <Grid item xs={12}>
              <FramedBox label="Projektvyer"
                slotProps={{
                  content: { display: "flex", flexDirection: "row", flexWrap: "nowrap", justifyContent: "space-between", alignItems: "center" },
                }}
              >
                <ProjectViewSelector
                  onSelect={(index, viewId) => showViewsModal(viewId)}
                  selectedIndex={0}
                  slotProps={{
                    root: {
                      maxWidth: "100%",
                      minWidth: "0%",
                      //".MuiTabs-scroller": {
                      //  pointerEvents: "none",
                      //},
                    },
                    tabs: {
                      ".MuiTabs-scrollButtons": {
                        opacity: "0.8 !important",
                        width: "20px !important",
                      }
                    },
                  }}
                  tabsData={projectTabs}
                />
                <IconButton
                  aria-label="edit"
                  edge="end"
                  onClick={() => showViewsModal()}
                  sx={{ mr: -1, ".MuiSvgIcon-root": { opacity: 0.8 } }}
                  size="small"
                >
                  <EditOutlinedIcon />
                </IconButton>
              </FramedBox>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ mx: 3, my: 1, p: 0 }}>
          <GrayButton
            onClick={handleClose}
          >
            <FormattedMessage id="btn.cancel" />
          </GrayButton>
          <PrimaryButton
            disabled={isSubmitting}
            onClick={isEditMode ? handleUpdate : handleCreate}
            size="small"
            variant="contained"
          >
            <FormattedMessage id={isEditMode ? "btn.save" : "btn.create"} />
          </PrimaryButton>
        </DialogActions>

      </Dialog>

      {showProjectViewsModal && (
        <ProjectViewsModal
          initialViews={projectTabs}
          open={showProjectViewsModal}
          onCancel={hideViewsModal}
          onUpdate={handleViewsUpdate}
          projectFields={internalProject.projectFields}
          selectedViewId={selectedViewIdRef.current}
        />
      )}
    </>
  );
}

ProjectModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onSaved: PropTypes.func,
  project: PropTypes.shape({
    projectId: PropTypes.number,
    name: PropTypes.string,
    description: PropTypes.string,
    linkedFolderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    projectFields: PropTypes.arrayOf(
      PropTypes.shape({
        fieldId: PropTypes.number,
        name: PropTypes.string,
        type: PropTypes.number,
        jsonFieldSettings: PropTypes.object,
      })
    ),
    projectViews: PropTypes.arrayOf(
      PropTypes.shape({
        viewId: PropTypes.number,
        name: PropTypes.string,
        type: PropTypes.number,
        jsonViewSettings: PropTypes.object,
      })
    ),
    siteId: PropTypes.number,
  }),
  siteId: PropTypes.number.isRequired,
};

ProjectModal.displayName = "ProjectModal";

export default ProjectModal;
